/**
 * CaptureFlourish — brief, non-blocking visual effect that plays on
 * the captured square whenever a move produces a capture (Feb 2026).
 *
 * Scope & isolation
 * -----------------
 * This component is intentionally 100% self-contained. It relies on
 * ONE piece of data that already exists universally across every game
 * mode this app supports: Game.jsx computes `captureFlashSquares` from
 * the latest `move_history` entry whenever `latest.captured && latest.to`,
 * and applies a `.capture-flash` class to the exact `.cell`
 * receiving the capture (see Game.jsx ~line 474-477 and GameBoard.jsx
 * ~line 245). No engine, no validator, no capture-tracking logic is
 * touched — this component just observes an existing DOM signal.
 *
 * How it works
 * ------------
 *   1. On mount it installs (idempotently) ONE MutationObserver on
 *      document.body watching for `class` attribute changes with
 *      `attributeOldValue: true`. Whenever an element gains the
 *      `capture-flash` class it didn't previously have AND that
 *      element is a `.cell`, that's the transition we want.
 *   2. It injects a short-lived (~460ms) absolutely-positioned
 *      overlay into that cell — a radial crimson burst + six orange
 *      shard rays — then removes it. `pointer-events: none` so the
 *      board stays fully interactive; the animation never blocks a
 *      subsequent click, move, or promotion.
 *   3. Keyframe styles are injected once into <head> by JS on first
 *      install so no external CSS file is modified.
 *
 * Sound
 * -----
 * The existing capture sound cue lives in SoundContext and is fired
 * by Game.jsx as part of its capture-detection block. This component
 * makes no sound. No new sound assets are added.
 *
 * Cross-mode
 * ----------
 * Because the trigger is the `.capture-flash` class (which itself is
 * driven off the mode-agnostic `move_history[-1].captured` check in
 * Game.jsx), this component fires for captures in every mode that
 * already tracks captures — chess-family, FPC-family, checkers-family,
 * and four-way-checkers — without a single mode-specific branch here.
 *
 * Replay guard
 * ------------
 * Game.jsx passes `captureFlashSquares={inReplay ? [] : ...}` so
 * during history scrubbing NO cell ever gains the `.capture-flash`
 * class — the observer therefore stays silent during replay. No
 * replay-detection logic is needed inside this file.
 */
import { useEffect } from "react";


const OBSERVER_KEY = "__captureFlourishInstalled__";
const STYLE_ID = "capture-flourish-styles";
const OVERLAY_CLASS = "capture-flourish";
const DURATION_MS = 460;   // total flourish lifespan, kept inside 300-500ms


function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  // Keyframes kept local to the component (injected into <head> on
  // first install) so App.css is untouched. All names are prefixed
  // with `capture-flourish-` to avoid colliding with the existing
  // `.capture-flash` background animation from App.css.
  s.textContent = `
    @keyframes capture-flourish-burst {
      0%   { transform: scale(0.30); opacity: 0; }
      35%  { transform: scale(1.00); opacity: 0.9; }
      100% { transform: scale(1.25); opacity: 0; }
    }
    @keyframes capture-flourish-shard {
      0%   { transform: scale(0);   opacity: 0; }
      30%  {                        opacity: 1; }
      100% { transform: scale(1.15); opacity: 0; }
    }
    .${OVERLAY_CLASS}__burst {
      position: absolute; inset: 0;
      background: radial-gradient(circle,
        rgba(255,110,110,0.78) 0%,
        rgba(255,60,60,0.35)   38%,
        rgba(0,0,0,0)          70%);
      animation: capture-flourish-burst ${DURATION_MS}ms ease-out forwards;
      will-change: transform, opacity;
    }
    .${OVERLAY_CLASS}__shards {
      position: absolute; inset: 0; overflow: visible;
    }
    .${OVERLAY_CLASS}__shard {
      transform-origin: 50% 50%;
      animation: capture-flourish-shard ${DURATION_MS}ms
                 cubic-bezier(.2,.9,.35,1.05) forwards;
      animation-delay: calc(var(--i) * 18ms);
      will-change: transform, opacity;
      stroke: #ff9a3a;
      stroke-width: 2.6;
      stroke-linecap: round;
    }
  `;
  document.head.appendChild(s);
}


function spawnFlourish(cell) {
  // The board-cell element already carries `position: relative`
  // (established by App.css for existing overlays like the kinging
  // flourish); guard anyway so the overlay always positions
  // correctly, without mutating any long-lived style if the base
  // rule ever changes.
  const computed = getComputedStyle(cell);
  if (computed.position === "static") cell.style.position = "relative";

  const overlay = document.createElement("div");
  overlay.className = OVERLAY_CLASS;
  overlay.setAttribute("aria-hidden", "true");
  overlay.setAttribute("data-testid", "capture-flourish");
  Object.assign(overlay.style, {
    position: "absolute",
    inset: "0",
    pointerEvents: "none",   // NEVER intercepts clicks — non-blocking guarantee
    zIndex: "5",
    borderRadius: "inherit",
    overflow: "hidden",
  });

  const shards = Array.from({ length: 6 }, (_, i) => {
    const a = (i * 60) * Math.PI / 180;
    const x2 = Math.cos(a) * 44;
    const y2 = Math.sin(a) * 44;
    return `<line x1="0" y1="0" x2="${x2.toFixed(2)}" y2="${y2.toFixed(2)}" class="${OVERLAY_CLASS}__shard" style="--i:${i}" />`;
  }).join("");

  overlay.innerHTML =
    `<div class="${OVERLAY_CLASS}__burst"></div>` +
    `<svg class="${OVERLAY_CLASS}__shards" viewBox="-50 -50 100 100" preserveAspectRatio="xMidYMid meet">${shards}</svg>`;

  cell.appendChild(overlay);
  window.setTimeout(() => {
    // Defensive: the cell may have unmounted (route change, replay
    // scrubbing) before the timer fires. `contains` guards it.
    if (cell.contains(overlay)) {
      try { cell.removeChild(overlay); } catch { /* concurrent unmount */ }
    }
  }, DURATION_MS + 60);
}


function installOnce() {
  if (window[OBSERVER_KEY]) return;
  window[OBSERVER_KEY] = true;
  injectStyles();

  const CAPTURE_FLASH_CLASS = "capture-flash";
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type !== "attributes" || m.attributeName !== "class") continue;
      const el = m.target;
      if (!(el instanceof HTMLElement)) continue;
      if (!el.classList.contains("cell")) continue;
      // Fire ONLY on the transition to having capture-flash — the
      // class is toggled off ~600ms later by Game.jsx, and that
      // removal must not re-fire the effect.
      const hasFlashNow = el.classList.contains(CAPTURE_FLASH_CLASS);
      if (!hasFlashNow) continue;
      const oldClasses = (m.oldValue || "").split(/\s+/);
      const hadFlashBefore = oldClasses.indexOf(CAPTURE_FLASH_CLASS) !== -1;
      if (hadFlashBefore) continue;
      spawnFlourish(el);
    }
  });
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ["class"],
    attributeOldValue: true,
    subtree: true,
  });
  // Stashed only for tests / dev — we never actually disconnect it,
  // as the app lifetime IS the observer lifetime.
  window[OBSERVER_KEY + "Observer"] = observer;
}


export default function CaptureFlourish() {
  useEffect(() => {
    installOnce();
    // Deliberate: no unmount / disconnect. The single global observer
    // is app-lifetime and idempotent — installOnce() no-ops if called
    // again from another CaptureFlourish instance (e.g. React strict
    // mode's double-mount).
  }, []);
  return null;
}
